/**
 * @file UserProvider.js
 * @module src/js/contexts/UserProvider
 * @description
 * **Provides user-related context to the EDALab frontend.**
 * - Initializes `uid`, `ugroup`, and `lastLogin` states from `localStorage`, defaulting to `"Guest"`.
 * - Persists any updates to `uid`, `ugroup`, `lastLogin` in `localStorage`.
 * - Manages `isLoggingOut` state to indicate if a logout process is ongoing.
 * - Exposes `uid`, `setUid`, `ugroup`, `setUgroup`, `lastLogin`, `setLastLogin`, `isLoggingOut`, `setIsLoggingOut` via React Context to all child components.
 * - Please use `useUser()` to access or update the current user state.
 *
 * @version
 * <p>
 *  2025-07-22: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add isLoggingOut state to manage logout process. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add lastLogin state persisted in localStorage. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { createContext, useState, useEffect, useContext } from 'react';

const userContext = createContext();

export default function UserProvider({ children }) {
    // Load from localStorage (or default value)
    // If localStorage is not used, the state will be reset on every page refresh
    const [uid, setUid] = useState(localStorage.getItem("uid") || "Guest");
    const [ugroup, setUgroup] = useState(localStorage.getItem("ugroup") || "Guest");
    const [lastLogin, setLastLogin] = useState(localStorage.getItem("lastLogin") || "N/A");
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    useEffect(() => {
        // Save state to localStorage whenever it changes
        localStorage.setItem("uid", uid);
    }, [uid]);

    useEffect(() => {
        localStorage.setItem("ugroup", ugroup);
    }, [ugroup]);

    useEffect(() => {
        localStorage.setItem("lastLogin", lastLogin);
    }, [lastLogin]);

    return (
        <userContext.Provider value={{
            uid, setUid,
            ugroup, setUgroup,
            lastLogin, setLastLogin,
            isLoggingOut, setIsLoggingOut
        }}>
            {children}
        </userContext.Provider>
    );
}

export const useUser = () => useContext(userContext);